import paper from 'paper';

export interface LayerEventHandlers {
  readonly onClick: () => void;
  readonly onMouseUp: (startDragPoint: paper.Point, endDragPoint: paper.Point, event: paper.MouseEvent) => void;

  readonly isPanEnabled: (event: paper.MouseEvent) => boolean;
  readonly onPanDrag: (viewDeltaX: number, viewDeltaY: number) => void;
}

export default function registerLayerEvents(layer: paper.Layer, layerEventHandlers: LayerEventHandlers) {
  let dragged: boolean;
  let startDragPoint: paper.Point;

  let currentlyPanning = false;

  function startPanning() {
    if (currentlyPanning) return;

    // paper.js drag deltas are reported in project space, which is what panning modifies. :(
    // so native mouse movement events are better to drive panning -- we don't have to worry about ^^ and can guarantee
    //   that the page will track the cursor directly.
    document.addEventListener('mousemove', handlePanMouseMove, { passive: true });
    document.addEventListener('mouseup', stopPanning);

    currentlyPanning = true;
  }

  function stopPanning() {
    currentlyPanning = false;

    document.removeEventListener('mousemove', handlePanMouseMove);
    document.removeEventListener('mouseup', stopPanning);
  }

  function handlePanMouseMove(event: MouseEvent) {
    if (event.movementX !== 0 || event.movementY !== 0) {
      layerEventHandlers.onPanDrag(event.movementX, event.movementY);
    }
  }

  layer.onMouseDown = function handleLayerMouseDown(event: paper.MouseEvent) {
    stopPanning();
    startDragPoint = new paper.Point(event.point);
    dragged = false;
  };

  layer.onMouseDrag = function handleLayerMouseDrag(event: paper.MouseEvent) {
    if (currentlyPanning) {
      event.stopPropagation();
      return;
    }

    if (event.delta.x === 0 && event.delta.y === 0) {
      return;
    }

    event.stopPropagation();
    dragged = true;

    if (layerEventHandlers.isPanEnabled(event)) {
      startPanning();
      layerEventHandlers.onPanDrag(event.event.movementX, event.event.movementY);
    }
  };

  layer.onMouseUp = function handleLayerMouseUp(event: paper.MouseEvent) {
    if (currentlyPanning) {
      stopPanning();
      return;
    }

    if (dragged) {
      const endDragPoint = new paper.Point(event.point);

      layerEventHandlers.onMouseUp(startDragPoint, endDragPoint, event);
    }
  };

  layer.onClick = function handleLayerClick() {
    if (!dragged) {
      layerEventHandlers.onClick();
    }
  };
}
